import { formatCurrencyFromCents, withRetry } from "src/utils";
import { STOREFRONT_ENDPOINT } from "../consts";
import { PRODUCTS_PRICES_QUERY } from "./query";
import { WishlistProductNode, WishlistProductsResponse } from "./types";

const REQUEST_TIMEOUT_MS = 15000;

const getVariantId = (anchor: HTMLAnchorElement) => {
  const url = new URL(anchor.href);

  return `gid://shopify/ProductVariant/${url.searchParams.get("variant")}`;
};

export const getVariantIds = (wishlistProductLinks: HTMLAnchorElement[]) => {
  return wishlistProductLinks.map((link) => {
    return getVariantId(link);
  });
};

export const getLinks = () => {
  return Array.from(
    document.querySelectorAll<HTMLAnchorElement>(".wcPcMedia a")
  );
};

export const areVariantIdsEqual = (
  variantIds1: string[],
  variantIds2: string[]
) => {
  return variantIds1.every((id) => variantIds2.includes(id));
};

const queryWishlistProducts = async (
  variantIds: string[]
): Promise<WishlistProductsResponse> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(STOREFRONT_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": window.STOREFRONT_TOKEN,
      },
      body: JSON.stringify({
        query: PRODUCTS_PRICES_QUERY,
        variables: {
          variantIds,
        },
      }),
      signal: controller.signal,
    });
  } catch {
    clearTimeout(timeout);
    return { data: { nodes: [] } };
  } finally {
    clearTimeout(timeout);
  }

  if (!res.ok) {
    console.error("Failed to fetch product variants by product ids", res);
    return { data: { nodes: [] } };
  }

  const payload = await res.json();

  return payload as WishlistProductsResponse;
};

export const getWcProductCards = () => {
  return Array.from(document.querySelectorAll<HTMLElement>(".wcProductCard"));
};

export const getMappedWishlistVariants = (wishlistProducts: HTMLElement[]) => {
  const mapVariantIdsToVariants = wishlistProducts.reduce((acc, product) => {
    const variant = product.querySelector<HTMLAnchorElement>(".wcPcMedia a");

    if (!variant) return acc;

    acc[getVariantId(variant)] = product;

    return acc;
  }, {});

  return mapVariantIdsToVariants;
};

export const getProductVariants = async () => {
  const links = getLinks();
  const variantIds = getVariantIds(links);

  const products = await withRetry(
    () => queryWishlistProducts(variantIds),
    3,
    100
  );
  if (!products.data.nodes.length) return [];

  return products.data.nodes;
};

export const preparePrice = (
  item: WishlistProductNode,
  mappedVariants: Record<string, HTMLElement>
) => {
  const wishlistProduct = mappedVariants[item.id];
  if (!wishlistProduct) return;

  const priceElement =
    wishlistProduct.querySelector<HTMLElement>(".wcPcPrice .money");
  if (!priceElement) return;

  const footerElement =
    wishlistProduct.querySelector<HTMLElement>(".wcPcFooter");
  if (!footerElement) return;

  const currentPrice = parseFloat(item.price.amount);

  let finalPriceContent = `From ${formatCurrencyFromCents(currentPrice, "USD")}`;

  const condition = item.quantityAvailable < 1 || currentPrice === 0;

  footerElement.classList.toggle("disabled", condition);
  if (condition) {
    finalPriceContent = "Price on application.";
  }

  priceElement!.textContent = finalPriceContent;
};
